// frontend/src/components/common/CopyButton.jsx
import { useEffect, useRef, useState } from "react";
import { CheckIcon, CopyIcon } from "./Icon";
import "./ui.css";

async function writeClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  }
}

/**
 * Copies `text` (or the result of `getText()`) to the clipboard.
 * variant: icon (square, default) | text (icon + label)
 * size: sm | md; label: accessible name / visible label; onCopied(ok)
 */
export default function CopyButton({ text, getText, label = "Copy", copiedLabel = "Copied", variant = "icon", size = "sm", onCopied, className = "" }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleCopy = async () => {
    const value = getText ? getText() : text;
    const ok = await writeClipboard(value ?? "");
    onCopied?.(ok);
    if (!ok) return;
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1800);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={`copy-btn copy-btn--${variant} copy-btn--${size}${copied ? " is-copied" : ""} ${className}`.trim()}
      aria-label={copied ? copiedLabel : label}
      title={copied ? copiedLabel : label}
    >
      {copied ? <CheckIcon size={15} /> : <CopyIcon size={15} />}
      {variant === "text" && <span>{copied ? copiedLabel : label}</span>}
      <span className="sr-only" aria-live="polite">{copied ? copiedLabel : ""}</span>
    </button>
  );
}
